import {validateReferenceInstrumentEvidence} from './referenceInstrumentEvidence.js';
import {assertDistinctPerimeterReadings} from './typedPerimeterEvidence.js';
import { uid } from './constants.js';
import { validPolygon } from './editing.js';
import { fieldReferenceTarget } from './fieldReferences.js';
import { parseRecordedManualLength, manualLengthMetadata } from './manualLength.js';

export const TYPED_PERIMETER_SEGMENT_LIMIT = 63;
const EPS = 1e-7;
const finite = (value, min, max) => Number.isFinite(value) && value >= min && value <= max;
const validID = value => typeof value === 'string' && value.trim() === value && value.length > 0 && [...value].length <= 80;
const point = value => value && finite(value.x, -100000, 100000) && finite(value.y, -100000, 100000);
const norm = direction => ((direction % 360) + 360) % 360;
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
function trig(direction) {
  const radians = direction * Math.PI / 180;
  const exact = value => Math.abs(value) < 1e-15 ? 0 : value;
  return { x: exact(Math.cos(radians)), y: exact(Math.sin(radians)) };
}
function validateList(plan, name, limit) {
  const list = plan[name];
  if (!Array.isArray(list) || list.length > limit) throw new Error(`This floor has an invalid or oversized ${name} list.`);
  const seen = new Set();
  for (const item of list) {
    if (!item || !validID(item.id) || seen.has(item.id)) throw new Error(`The ${name} need valid unique IDs before a perimeter can be added.`);
    seen.add(item.id);
  }
}
function manualEvidence(input, metres, plan) {
  if (input === undefined) return undefined;
  const parsed = parseRecordedManualLength(input, { min: .1, max: 10000 });
  if (!parsed.ok || Math.abs(parsed.metres - metres) > 1e-9 || parsed.enteredUnit !== input.enteredUnit || parsed.explicitUnit !== input.explicitUnit) throw new Error('An entered segment no longer matches its original length text. Re-enter that length.');
  return manualLengthMetadata(parsed);
}
function distanceToEdge(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy);
}
function validateOutline(vertices) {
  // Translate for stable shoelace area arithmetic near the Site coordinate bounds.
  const local = vertices.map(p => ({ x: p.x - vertices[0].x, y: p.y - vertices[0].y }));
  if (!validPolygon(local)) throw new Error('The perimeter must enclose at least 0.01 m² and cannot cross, touch or collapse itself.');
  for (let i = 0; i < vertices.length; i++) {
    for (let j = i + 1; j < vertices.length; j++) {
      if (distance(vertices[i], vertices[j]) <= EPS) throw new Error('Two perimeter corners coincide. Remove the duplicate corner.');
    }
    const a = vertices[i], b = vertices[(i + 1) % vertices.length], c = vertices[(i + 2) % vertices.length];
    // Adjacent edges share one corner, but may not fold back along one another.
    if (distanceToEdge(c, a, b) <= EPS && distance(b, c) > EPS || distanceToEdge(a, b, c) <= EPS && distance(a, b) > EPS) throw new Error('Two adjoining perimeter segments fold back or overlap. Review their directions.');
    for (let j = i + 2; j < vertices.length; j++) {
      if (i === 0 && j === vertices.length - 1) continue;
      const d = vertices[j], e = vertices[(j + 1) % vertices.length];
      if (Math.min(distanceToEdge(d, a, b), distanceToEdge(e, a, b), distanceToEdge(a, d, e), distanceToEdge(b, d, e)) <= EPS) throw new Error('Non-adjoining perimeter segments touch or nearly touch. Review the corner positions.');
    }
  }
}

/** Create a new centreline perimeter; never reshape existing walls or solve
 * measured constraints. Each typed direction remains an assumption. The final
 * edge returns to the origin and is explicitly calculated/unmeasured.
 * Memoize the returned review and apply its doc once to keep generated IDs stable. */
export function reviewTypedPerimeter(plan, { name, height, origin, segments, originReference } = {}) {
  if (!plan || plan._siteLevels || plan._groundSelections || plan._sceneWalls || !validID(plan.activeFloorId)) throw new Error('Choose an editable floor before entering a perimeter.');
  validateList(plan, 'walls', 4000); validateList(plan, 'areas', 200);
  if (typeof name !== 'string' || !name.trim() || [...name.trim()].length > 120) throw new Error('Enter a perimeter name of at most 120 characters.');
  if (!finite(height, .1, 60)) throw new Error('Enter a wall and ceiling height from 0.1 to 60 m.');
  if (!point(origin)) throw new Error('Enter an origin within ±100000 m in Site coordinates.');
  if (!Array.isArray(segments) || segments.length < 2 || segments.length > TYPED_PERIMETER_SEGMENT_LIMIT) throw new Error('Enter 2–63 segments. The calculated closing wall gives 3–64 corners.');
  if (plan.walls.length + segments.length + 1 > 4000 || plan.areas.length + 1 > 200) throw new Error('This perimeter would exceed the floor limit of 4000 walls or 200 areas.');
  let reference;
  if (originReference !== undefined) {
    if (!validID(originReference)) throw new Error('Choose a valid named origin reference.');
    const target = fieldReferenceTarget(plan, originReference);
    if (origin.x !== target.x || origin.y !== target.y) throw new Error('The origin no longer matches the named reference. Choose it again or enter an independent origin.');
    reference = target.fieldReference;
  }
  const vertices = [{ x: origin.x, y: origin.y }], entered = [];
  for (const segment of segments) {
    if (!segment || !finite(segment.metres, .1, 10000) || !finite(segment.direction, -360000, 360000)) throw new Error('Each segment needs a length from 0.1 to 10000 m and a finite direction within ±360000°.');
    const direction = norm(segment.direction), vector = trig(direction), last = vertices.at(-1);
    const next = { x: last.x + segment.metres * vector.x, y: last.y + segment.metres * vector.y };
    if (!point(next)) throw new Error('An entered perimeter corner extends beyond supported Site coordinates.');
    let original;
    if(Object.hasOwn(segment,'sourceEvidence')){
      if(Object.hasOwn(segment,'manualInput'))throw new Error('Choose manual entry or an original instrument reading for each segment, not both.');
      original={lengthSource:'instrument-recorded',sourceEvidence:validateReferenceInstrumentEvidence(segment.sourceEvidence,segment.metres)};
    }else{
      const manualInput=manualEvidence(segment.manualInput,segment.metres,plan);
      original={lengthSource:'operator-entered',...(manualInput?{manualInput}:{})};
    }
    entered.push({metres:segment.metres,direction,enteredDirection:segment.direction,directionSource:'typed-assumed',...original});
    vertices.push(next);
  }
  assertDistinctPerimeterReadings(entered);
  const last = vertices.at(-1), closingMetres = distance(last, vertices[0]);
  if (!finite(closingMetres, .1, 300000)) throw new Error('The calculated closing wall must be at least 0.1 m. Omit the final measured segment and review the calculated return to the origin.');
  validateOutline(vertices);
  const used = new Set([...plan.walls, ...plan.areas].map(item => item.id));
  const nextID = prefix => {
    for (let attempt = 0; attempt < 16; attempt++) { const id = uid(prefix); if (!used.has(id)) { used.add(id); return id; } }
    throw new Error('Could not allocate a unique perimeter identity. Review the perimeter again.');
  };
  const areaID = nextID('perimeter-area-'), createdAt = new Date().toISOString();
  const common = { source: 'typed-perimeter', perimeterID: areaID, recordedFloorID: plan.activeFloorId, createdAt, basis: 'wall-centreline' };
  const closingDirection = norm(Math.atan2(origin.y - last.y, origin.x - last.x) * 180 / Math.PI);
  const walls = vertices.map((a, i) => {
    const b = vertices[(i + 1) % vertices.length];
    return { id: nextID('perimeter-wall-'), name: `${name.trim()} wall ${i + 1}`, kind: 'solid', h: height,
      x1: a.x, y1: a.y, x2: b.x, y2: b.y,
      layoutEvidence: { ...common, segmentIndex: i + 1,
        ...(i < entered.length ? structuredClone(entered[i]) : { metres: closingMetres, direction: closingDirection, lengthSource: 'calculated', directionSource: 'calculated', unmeasured: true }) } };
  });
  const closing = { wallID: walls.at(-1).id, metres: closingMetres, direction: closingDirection, source: 'calculated', unmeasured: true };
  const area = { id: areaID, name: name.trim(), kind: 'floor', environment: 'indoor', purpose: 'building', roofHeight: height,
    pts: structuredClone(vertices), layoutEvidence: { ...common, origin: { x: origin.x, y: origin.y }, ...(reference ? { originReference: structuredClone(reference) } : {}),
      segments: structuredClone(entered), closing: structuredClone(closing), heightSource: 'operator-entered', height } };
  const doc = structuredClone(plan); doc.walls.push(...walls); doc.areas.push(area); doc.review = null;
  return { doc, geometry: structuredClone({ walls, areas: [area], openings: [] }), vertices: structuredClone(vertices), closing,
    warnings: [
      'The closing wall is calculated and unmeasured. Check its length and the shape with independent measurements.',
      'Directions are typed assumptions, not instrument-measured angles. Dimensions describe wall centrelines; inside-face readings may need a reviewed offset.',
      'Existing walls, rooms and objects stay unchanged. Review overlaps, shared boundaries and camera coverage before applying.'
    ] };
}
